<style lang="scss">
  @use "../../assets/css/design-system/colors";
  @use "../../assets/css/design-system/utils";
  @use "../../assets/css/design-system/mixins";

  li {
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    border-radius: 0; // 0 otherwise we hide the border radius of the underline
    display: inline-table;
    gap: 0;
    margin: 0;
    padding: 0;
    background-color: rgba(0, 0, 0, 0);
    @include colors.color(textGrey);
    position: relative;
    cursor: pointer;
    &.disabled {
      cursor: default;
    }

    &.secondary {
      border-radius: 12px 12px 0px 0px;
    }
    &.secondary-pill {
      gap: utils.in_rem(12px);
      margin-right: utils.in_rem(8px); // Add gaps between the tabs
      margin-bottom: utils.in_rem(12px); // Don't let tabs touch the content

      &.small {
        border-radius: 100px;
      }
      &.large {
        border-radius: 170px;
      }
    }
    &.vertical {
      border-radius: 10px;
    }

    > div {
      display: flex;
      align-items: center;

      &.with-badge-or-icon {
        gap: utils.in_rem(8px);
      }

      &.small {
        height: utils.in_rem(32px);
        &.secondary-pill {
          height: utils.in_rem(24px);
        }
      }

      &.large {
        height: utils.in_rem(36px);
      }

      padding: utils.in_rem(0px) utils.in_rem(8px);

      > span {
        &.small {
          @include mixins.tabLabel-small();
        }
        &.large {
          @include mixins.tabLabel();
        }

        text-transform: uppercase;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;

        // Add a bit of space after the headers which is captured by the highlight
        &.modal-vertical {
          margin-right: utils.in_rem(24px);
        }

        .padlock-container {
          margin-left: utils.in_rem(4px);
        }
      }
    }

    > .underline {
      height: 1.5px;
      width: 100%;
      border-radius: 24px;
      background-color: transparent;
    }

    @mixin text-colors() {
      @include colors.color(textBlack);
      &:hover,
      &.active {
        @include colors.color(primary_50);
      }
      &.disabled {
        @include colors.color(textDisabledGrey);
      }
    }

    &.primary {
      @include text-colors();
      background-color: transparent;
      &:hover > .underline {
        @include colors.background-color(primary_10);
      }

      &.active > .underline {
        @include colors.background-color(primary_50);
      }

      &.disabled > .underline {
        background-color: transparent;
      }
    }

    &.secondary {
      @include text-colors();
      @include colors.background-color(interfaceSwGrey_10);
      &:hover {
        @include colors.background-color(interfaceSwGrey_10);
      }
      &.active {
        @include colors.background-color(primary_5);
      }
      &.disabled {
        @include colors.background-color(interfaceDisabledGrey_5);
      }
    }

    &.secondary-pill {
      @include text-colors();
      @include colors.background-color(interfaceSwGrey_4);
      &:hover {
        @include colors.background-color(interfaceSwGrey_10);
      }
      &.active {
        @include colors.background-color(primary_5);
      }
      &.disabled {
        @include colors.background-color(interfaceDisabledGrey_5);
      }
    }

    &.vertical {
      @include text-colors();
      background-color: transparent;
      &:hover {
        @include colors.background-color(primary_5);
      }
      &.active {
        @include colors.background-color(primary_10);
      }
    }
  }

  .icon-container-large {
    width: utils.in_rem(16px);
    height: utils.in_rem(16px);
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }

  .icon-container-small {
    width: utils.in_rem(12px);
    height: utils.in_rem(12px);
    display: flex;
    flex-shrink: 0;
    align-items: center;
    justify-content: center;
  }
</style>

<script>
  import Badge from "./Badge.svelte";
  import Icon from "./Icon.svelte";

  export let label;
  export let leadingIcon = "";
  export let badge = "";
  export let badgeProps = {
    size: "large",
    type: "number",
    contrast: "high",
    inline: true,
  };
  export let active = false;
  export let disabled = false;
  export let onclick = () => {};
  export let showPadlock = false;

  /** @type {"primary" | "secondary" | "secondary-pill" | "vertical"} */
  export let type = "primary";
  export let skeleton = false;
  export let modalVertical = false;

  /** @type {"small" | "large"} */
  export let size = "large";

  let cls = "";
  export { cls as class };

  const keydown = (e) => {
    if (!["Enter", " "].includes(e.key)) return;
    e.preventDefault();
    onclick(e);
  };
</script>

<li
  class="{cls} {type} {size}"
  class:active
  class:disabled
  tabindex="0"
  on:click={disabled ? () => {} : onclick}
  on:keydown={disabled ? () => {} : keydown}
  role="tab"
  {...$$restProps}
>
  <div class="{size} {type}" class:with-badge-or-icon={leadingIcon || badge}>
    {#if leadingIcon}
      <div class="icon-container-{size}">
        <Icon
          class={leadingIcon}
          size={size === "large" ? "medium" : "small"}
        />
      </div>
    {/if}
    <span class={size} class:skeleton class:modal-vertical={modalVertical}>
      {label}
      {#if showPadlock}
        <span class="padlock-container">
          <Icon class="fa-solid fa-lock" size="small" />
        </span>
      {/if}
    </span>
    {#if badge}
      <Badge value={badge} {...badgeProps} inline />
    {/if}
  </div>
  {#if type === "primary"}
    <div class="underline"></div>
  {/if}
</li>
